import React, { useState, useEffect } from 'react';
import {
  Box, 
  Heading, 
  Text, 
  List, 
  ListItem,
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  SimpleGrid,
  Flex,
  Spinner,
  Alert,
  Badge,
  Icon,
  Divider,
  useToast,
  Tag,
  Code,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  FormHelperText,
  Collapse
} from '@chakra-ui/react';
import { InfoIcon, DownloadIcon, SearchIcon, CloseIcon } from '@chakra-ui/icons';
import api from '../../services/api';

/**
 * Component for running export scripts and downloading their CSV output
 */
const ExportScripts = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [customKeywords, setCustomKeywords] = useState('');
  const [selectedScript, setSelectedScript] = useState(null);
  const toast = useToast();

  // Load available scripts on component mount
  useEffect(() => {
    fetchScripts();
  }, []);

  // Fetch available scripts from the backend
  const fetchScripts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/exports/scripts');
      setScripts(response.data);
    } catch (error) {
      console.error('Error fetching scripts:', error);
      toast({
        title: 'Error fetching scripts',
        description: error.response?.data?.error || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Execute a script and download the resulting CSV
  const executeScript = async (scriptId) => {
    try {
      // Validate that keywords are provided if this is the custom keywords script
      if (scriptId === 'search-custom-keywords') {
        if (!customKeywords.trim()) {
          toast({
            title: 'Keywords required',
            description: 'Please enter at least one keyword to search for.',
            status: 'warning',
            duration: 3000,
            isClosable: true,
          });
          return;
        }
      }
      
      setExecuting(scriptId);
      
      // Handle custom keywords for the custom keywords script
      let url = `/api/exports/execute/${scriptId}`;
      if (scriptId === 'search-custom-keywords') {
        // Properly format keywords: split by commas and clean up
        const keywordList = customKeywords
          .split(/,/) // Split only by comma to preserve multi-word keywords
          .map(k => k.trim())
          .filter(k => k.length > 0);
          
        if (keywordList.length > 0) {
          // Important: Pass comma-separated list to preserve multi-word keywords
          const keywordsParam = keywordList.join(',');
          console.log('Sending keywords:', keywordsParam);
          url += `?keywords=${encodeURIComponent(keywordsParam)}`;
        }
      }
      
      // Step 1: Execute the script
      const executeResponse = await api.get(url);
      
      if (executeResponse.data.success) {
        toast({
          title: 'Script executed successfully',
          description: 'Downloading CSV file...',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        try {
          // Step 2: Download the file with authentication
          const downloadPath = executeResponse.data.path;
          const filename = executeResponse.data.filename;
          
          // Make an authenticated request for the file
          const downloadResponse = await api.get(downloadPath, {
            responseType: 'blob', // Important: this tells axios to handle the response as a binary blob
          });
          
          // Create a blob URL and trigger download
          const blob = new Blob([downloadResponse.data], { 
            type: downloadResponse.headers['content-type'] || 'text/csv' 
          });
          const url = window.URL.createObjectURL(blob);
          
          // Create and click a temporary download link
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url); // Free up memory
          
          console.log('File downloaded successfully');
        } catch (downloadError) {
          console.error('Error downloading file:', downloadError);
          toast({
            title: 'Error downloading file',
            description: 'The script was executed but the file could not be downloaded.',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error executing script:', error);
      toast({
        title: 'Error executing script',
        description: error.response?.data?.error || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExecuting(null);
    }
  };

  return (
    <Box p={4}>
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="md">Export Scripts</Heading>
        <Button
          size="sm"
          onClick={fetchScripts}
          isLoading={loading}
          variant="outline"
        >
          Refresh
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" minH="200px">
          <Spinner mr={2} size="md" />
          <Text>Loading available scripts...</Text>
        </Flex>
      ) : scripts.length === 0 ? (
        <Card variant="outline">
          <CardBody>
            <Text textAlign="center">No export scripts available</Text>
          </CardBody>
        </Card>
      ) : (
        <List spacing={4}>
          {scripts.map((script) => (
            <ListItem 
              key={script.id} 
              p={5} 
              border="1px" 
              borderColor={selectedScript === script.id ? "blue.300" : "gray.200"} 
              borderRadius="md"
              boxShadow={selectedScript === script.id ? "md" : "sm"}
              _hover={{ boxShadow: 'md', borderColor: 'blue.100' }}
              onClick={() => setSelectedScript(selectedScript === script.id ? null : script.id)}
              cursor="pointer"
            >
              <Flex justify="space-between" align="flex-start" flexWrap="wrap">
                <Box width={script.id === 'search-custom-keywords' ? '100%' : '75%'}>
                  <Heading size="md">{script.name}</Heading>
                  <Text mt={2} color="gray.700" fontSize="md">{script.description}</Text>
                  
                  {script.keywords && script.keywords.length > 0 && (
                    <Flex mt={3} flexWrap="wrap" gap={2}>
                      <Text fontWeight="medium" color="gray.600">Keywords:</Text>
                      {script.keywords.map((keyword, idx) => (
                        <Tag key={idx} size="sm" colorScheme="blue" variant="subtle">
                          {keyword}
                        </Tag>
                      ))}
                    </Flex>
                  )}
                  
                  {script.outputFile && (
                    <Text mt={2} fontSize="sm" color="gray.500">
                      Output: <Code>{script.outputFile}</Code>
                    </Text>
                  )}
                  
                  {/* Custom keyword input for the custom keywords script - always visible for that script */}
                  {script.id === 'search-custom-keywords' && (
                    <Box 
                      mt={4} 
                      p={4} 
                      bg="blue.50" 
                      borderRadius="md" 
                      borderLeft="4px solid" 
                      borderLeftColor="blue.400"
                      boxShadow="sm"
                    >
                      <FormControl isRequired>
                        <FormLabel fontWeight="medium" color="blue.700">Search Keywords</FormLabel>
                        <InputGroup size="md">
                          <Input
                            placeholder="Enter keywords separated by commas (e.g., Delhi, Noida, Farm)"
                            value={customKeywords}
                            onChange={(e) => setCustomKeywords(e.target.value)}
                            pr="4.5rem"
                            bg="white"
                            borderColor="blue.200"
                            _hover={{ borderColor: "blue.300" }}
                            _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                          />
                          {customKeywords && (
                            <InputRightElement width="4.5rem">
                              <Button 
                                h="1.75rem" 
                                size="sm" 
                                onClick={() => setCustomKeywords('')}
                                colorScheme="blue"
                                variant="ghost"
                              >
                                <CloseIcon fontSize="xs" />
                              </Button>
                            </InputRightElement>
                          )}
                        </InputGroup>
                        <FormHelperText color="blue.700">
                          The script will search for leads containing any of these keywords (required)
                        </FormHelperText>
                        
                        {/* Show example tags for common searches */}
                        <Flex mt={2} flexWrap="wrap" gap={2}>
                          <Text fontSize="sm" color="blue.700">Quick add:</Text>
                          {['Noida', 'Delhi', 'Gurgaon', 'Chattarpur', 'Farm'].map(suggestion => (
                            <Tag 
                              key={suggestion} 
                              size="sm" 
                              colorScheme="blue" 
                              variant="subtle" 
                              cursor="pointer"
                              onClick={() => {
                                const keywords = customKeywords
                                  .split(/[,\s]+/)
                                  .map(k => k.trim())
                                  .filter(k => k);
                                if (!keywords.includes(suggestion)) {
                                  setCustomKeywords(customKeywords ? `${customKeywords}, ${suggestion}` : suggestion);
                                }
                              }}
                            >
                              +{suggestion}
                            </Tag>
                          ))}
                        </Flex>
                      </FormControl>
                    </Box>
                  )}
                </Box>
                {script.id === 'search-custom-keywords' ? (
                  <Flex width="100%" justifyContent="flex-end" mt={4}>
                    <Button
                      colorScheme="blue"
                      size="md"
                      isLoading={executing === script.id}
                      loadingText="Searching..."
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent toggling the selection
                        executeScript(script.id);
                      }}
                      leftIcon={<SearchIcon />}
                      width="200px"
                    >
                      Search & Download
                    </Button>
                  </Flex>
                ) : (
                  <Flex direction="column">
                    <Button
                      colorScheme="blue"
                      size="md"
                      isLoading={executing === script.id}
                      loadingText="Running..."
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent toggling the selection
                        executeScript(script.id);
                      }}
                      mt={1}
                      leftIcon={<DownloadIcon />}
                    >
                      Run & Download CSV
                    </Button>
                  </Flex>
                )}
              </Flex>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ExportScripts;
